/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect, useContext, useRef, forwardRef, useImperativeHandle } from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  TextField,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AlarmIcon from "@mui/icons-material/Alarm";
import StickyNote2Icon from "@mui/icons-material/StickyNote2";
import NoteAddOutlinedIcon from "@mui/icons-material/NoteAddOutlined";
import {
  DesktopDateTimePicker,
  LocalizationProvider,
} from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment, { Moment } from "moment";
import { getData } from "../../../Services/getData";
import { postData } from "../../../Services/postData";
import { deleteData } from "../../../Services/deleteData";
import { urlEnum } from "../../../Utils/urlEnum";
import { Vocabulary } from "../../../Utils/Vocabulary";
import { toast } from "react-toastify";
import { CacheContext } from "../../../Context/cacheContext";
import { europeanDateWithTime } from "../../../Utils/formatDate";

const BRAND = "#FF6B1A";

type ActivityNote = {
  id: number;
  kind: "note";
  note: string;
  user_id: string;
  created_at: string;
};

type ActivityFollowUp = {
  id: number;
  kind: "followup";
  note: string;
  follow_up_date: string;
  completed: boolean;
  user_id?: string;
};

type ActivityItem = ActivityNote | ActivityFollowUp;

type LeadActivityProps = {
  leadId: number;
  entityType?: string;
  onPendingFollowUpsChange?: (pending: ActivityFollowUp[]) => void;
  onFollowUpModeChange?: (active: boolean) => void;
};

export type LeadActivityHandle = {
  openFollowUp: () => void;
};

function isOverdue(date: string) {
  return moment(date).isBefore(moment());
}

const LeadActivity = forwardRef<LeadActivityHandle, LeadActivityProps>(function LeadActivity({
  leadId,
  entityType = "contactMessage",
  onPendingFollowUpsChange,
  onFollowUpModeChange,
}, ref) {
  const cacheContext = useContext(CacheContext);
  const [items, setItems] = useState<ActivityItem[]>([]);
  const [text, setText] = useState("");
  const [isFollowUp, setIsFollowUp] = useState(false);
  const [followUpDate, setFollowUpDate] = useState<Moment | null>(
    moment().add(1, "day").startOf("hour"),
  );
  const [loading, setLoading] = useState(false);
  const composerRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(ref, () => ({
    openFollowUp() {
      setIsFollowUp(true);
      onFollowUpModeChange?.(true);
      setTimeout(() => {
        composerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
    },
  }));

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId]);

  useEffect(() => {
    const pending = items.filter(
      (i): i is ActivityFollowUp => i.kind === "followup" && !i.completed
    );
    onPendingFollowUpsChange?.(pending);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  async function fetchAll() {
    try {
      const [notesRes, fuRes] = await Promise.all([
        getData(`${urlEnum.notesAll}/${leadId}/${entityType}`),
        getData(`${urlEnum.leadFollowUps}/${leadId}`),
      ]);

      const notes: ActivityNote[] = (notesRes.data?.notes || []).map(
        (n: any) => ({
          ...n,
          kind: "note" as const,
        }),
      );
      const followUps: ActivityFollowUp[] = (fuRes.data.followUps || []).map(
        (f: any) => ({
          ...f,
          kind: "followup" as const,
        }),
      );

      const merged: ActivityItem[] = [...notes, ...followUps].sort((a, b) => {
        const dateA =
          a.kind === "note"
            ? a.created_at
            : (a as ActivityFollowUp).follow_up_date;
        const dateB =
          b.kind === "note"
            ? b.created_at
            : (b as ActivityFollowUp).follow_up_date;
        return moment(dateB).diff(moment(dateA));
      });

      setItems(merged);
    } catch {
      setItems([]);
    }
  }

  async function handleAdd() {
    if (!text.trim()) return;
    if (isFollowUp && !followUpDate) return;

    setLoading(true);
    try {
      if (isFollowUp) {
        await postData(urlEnum.leadFollowUps, {
          lead_id: leadId,
          follow_up_date: followUpDate!.format("YYYY-MM-DD HH:mm:ss"),
          lead_type: entityType,
          note: text.trim(),
          user_id: localStorage.getItem("useId"),
        });
      } else {
        await postData(urlEnum.notes, {
          user_id: localStorage.getItem("useId"),
          entity_id: leadId.toString(),
          entity: entityType,
          note: text.trim(),
        });
      }
      setText("");
      setIsFollowUp(false);
      onFollowUpModeChange?.(false);
      setFollowUpDate(moment().add(1, "day").startOf("hour"));
      await fetchAll();
    } catch {
      toast.error(Vocabulary.addError);
    } finally {
      setLoading(false);
    }
  }

  async function completeFollowUp(id: number) {
    try {
      await postData(`${urlEnum.leadFollowUps}/${id}/complete`, {});
      await fetchAll();
    } catch {
      toast.error(Vocabulary.updateError);
    }
  }

  async function deleteFollowUp(id: number) {
    try {
      await deleteData(`${urlEnum.leadFollowUps}/${id}`);
      await fetchAll();
    } catch {
      toast.error(Vocabulary.deleteError);
    }
  }

  function getUserName(userId: string) {
    return (
      cacheContext.cache.users?.find((u: any) => u.id === parseInt(userId))
        ?.nume || "—"
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
      {/* ── COMPOSER ── */}
      <Box
        ref={composerRef}
        sx={{
          border: "1.5px solid",
          borderColor: isFollowUp ? BRAND : "divider",
          borderRadius: 2,
          bgcolor: "#fff",
          mb: 1.5,
          transition: "border-color 0.2s",
          overflow: "hidden",
        }}
      >
        <TextField
          fullWidth
          multiline
          minRows={2}
          maxRows={5}
          placeholder={
            isFollowUp
              ? `${Vocabulary.followUpNote}...`
              : `${Vocabulary.addNote}...`
          }
          value={text}
          onChange={(e) => setText(e.target.value)}
          variant="outlined"
          sx={{
            "& .MuiOutlinedInput-notchedOutline": { border: "none" },
            "& .MuiOutlinedInput-root": {
              borderRadius: 0,
              px: 1.5,
              pt: 1.5,
              pb: 1,
            },
          }}
        />

        {!isFollowUp && (
          <Box sx={{ px: 1.5, pb: 1.5 }}>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
              {(Vocabulary.followUpSuggestions as string[]).map((suggestion) => (
                <Chip
                  key={suggestion}
                  label={suggestion}
                  size="small"
                  onClick={() => setText(suggestion)}
                  variant={text === suggestion ? "filled" : "outlined"}
                  sx={{
                    fontSize: 11,
                    cursor: "pointer",
                    borderColor: `${BRAND}60`,
                    color: text === suggestion ? "#fff" : BRAND,
                    bgcolor: text === suggestion ? BRAND : "transparent",
                    "&:hover": { bgcolor: `${BRAND}15`, borderColor: BRAND },
                  }}
                />
              ))}
            </Box>
          </Box>
        )}

        {isFollowUp && (
          <Box sx={{ px: 1.5, pb: 1.5, display: "flex", flexDirection: "column", gap: 1.5 }}>
            {/* Suggestions */}
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
              {(Vocabulary.followUpSuggestions as string[]).map((suggestion) => (
                <Chip
                  key={suggestion}
                  label={suggestion}
                  size="small"
                  onClick={() => setText(suggestion)}
                  variant={text === suggestion ? "filled" : "outlined"}
                  sx={{
                    fontSize: 11,
                    cursor: "pointer",
                    borderColor: `${BRAND}60`,
                    color: text === suggestion ? "#fff" : BRAND,
                    bgcolor: text === suggestion ? BRAND : "transparent",
                    "&:hover": { bgcolor: `${BRAND}15`, borderColor: BRAND },
                  }}
                />
              ))}
            </Box>

            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DesktopDateTimePicker
                label={Vocabulary.followUpDate}
                value={followUpDate}
                minDateTime={moment()}
                onChange={(val) => setFollowUpDate(val)}
                slotProps={{ textField: { size: "small", fullWidth: true } }}
              />
            </LocalizationProvider>
          </Box>
        )}

        {/* Action bar */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 1.5,
            py: 1,
            borderTop: "1px solid",
            borderColor: "divider",
            bgcolor: "#fafafa",
          }}
        >
          {/* Follow-up quick-action button */}
          <Button
            size="small"
            variant={isFollowUp ? "contained" : "outlined"}
            startIcon={<AlarmIcon sx={{ fontSize: 15 }} />}
            onClick={() => { setIsFollowUp(!isFollowUp); onFollowUpModeChange?.(!isFollowUp); }}
            sx={{
              textTransform: "none",
              fontSize: 12,
              fontWeight: 600,
              borderRadius: 1.5,
              borderColor: isFollowUp ? BRAND : "divider",
              bgcolor: isFollowUp ? BRAND : "transparent",
              color: isFollowUp ? "#fff" : "text.secondary",
              "&:hover": {
                bgcolor: isFollowUp ? `${BRAND}dd` : `${BRAND}10`,
                borderColor: BRAND,
                color: isFollowUp ? "#fff" : BRAND,
              },
            }}
          >
            Follow-up
          </Button>

          <Box sx={{ flex: 1 }} />

          <Button
            variant="contained"
            size="small"
            startIcon={<NoteAddOutlinedIcon sx={{ fontSize: 15 }} />}
            onClick={handleAdd}
            disabled={!text.trim() || (isFollowUp && !followUpDate) || loading}
            sx={{
              textTransform: "none",
              fontSize: 12,
              fontWeight: 700,
              borderRadius: 1.5,
              bgcolor: BRAND,
              color: "#fff",
              "&:hover": { bgcolor: `${BRAND}dd` },
              "&:disabled": { bgcolor: "action.disabledBackground" },
            }}
          >
            {isFollowUp
              ? Vocabulary.scheduleFollowUp || "Programează"
              : Vocabulary.add}
          </Button>
        </Box>
      </Box>

      {/* ── TIMELINE DIVIDER ── */}
      <Divider sx={{ mb: 2 }}>
        <Typography
          variant="caption"
          sx={{
            color: "text.disabled",
            fontWeight: 600,
            letterSpacing: 1,
            textTransform: "uppercase",
            fontSize: 10,
          }}
        >
          {Vocabulary.activity || "Activitate"}
        </Typography>
      </Divider>

      {/* ── TIMELINE ── */}
      {items.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 5 }}>
          <StickyNote2Icon sx={{ fontSize: 32, color: "#d1d5db", mb: 1 }} />
          <Typography color="text.secondary" sx={{ fontStyle: "italic", fontSize: 13 }}>
            {Vocabulary.noNotes}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          {items.map((item, idx) => {
            const isFollowUp = item.kind === "followup";
            const completed = isFollowUp && item.completed;
            const overdue = isFollowUp && !item.completed && isOverdue(item.follow_up_date);
            const iconColor = isFollowUp
              ? completed ? "#10b981" : overdue ? "#ef4444" : BRAND
              : "#f59e0b";
            const chipLabel = isFollowUp
              ? (completed ? Vocabulary.followUpCompleted : overdue ? Vocabulary.overdueFollowUp : Vocabulary.upcomingFollowUp)
              : "NOTE";
            const itemDate = isFollowUp
              ? moment(item.follow_up_date).format(europeanDateWithTime)
              : moment.utc(item.created_at).local().format(europeanDateWithTime);

            return (
              <Box key={`${item.kind}-${item.id}`} sx={{ display: "flex", gap: 1.5 }}>
                {/* Spine */}
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0, width: 36 }}>
                  <Box sx={{ width: 36, height: 36, borderRadius: "50%", bgcolor: `${iconColor}20`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    {isFollowUp
                      ? completed
                        ? <CheckCircleIcon sx={{ fontSize: 16, color: iconColor }} />
                        : <AlarmIcon sx={{ fontSize: 16, color: iconColor }} />
                      : <StickyNote2Icon sx={{ fontSize: 16, color: iconColor }} />
                    }
                  </Box>
                  {idx < items.length - 1 && (
                    <Box sx={{ width: 2, flex: 1, bgcolor: "#e5e7eb", mt: 0.5, minHeight: 32 }} />
                  )}
                </Box>

                {/* Content */}
                <Box sx={{ flex: 1, pb: 3, opacity: completed ? 0.6 : 1 }}>
                  <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 0.75, gap: 1, flexWrap: "wrap" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                      <Chip
                        label={chipLabel}
                        size="small"
                        sx={{ bgcolor: `${iconColor}20`, color: iconColor, fontWeight: 700, fontSize: "0.7rem", border: "none" }}
                      />
                      <Typography variant="caption" color="text.secondary">{itemDate}</Typography>
                    </Box>
                    {isFollowUp && !completed && (
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<CheckCircleOutlineIcon sx={{ fontSize: 13 }} />}
                          onClick={() => completeFollowUp(item.id)}
                          sx={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "none", bgcolor: "#16a34a", color: "#fff", borderRadius: "8px", boxShadow: "0 1px 4px rgba(22,163,74,0.3)", "&:hover": { bgcolor: "#15803d" }, py: 0.5, px: 1.5 }}
                        >
                          {Vocabulary.markAsCompleted}
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<DeleteIcon sx={{ fontSize: 13 }} />}
                          onClick={() => deleteFollowUp(item.id)}
                          sx={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "none", py: 0.5, px: 1.5 }}
                        >
                          {Vocabulary.delete}
                        </Button>
                      </Box>
                    )}
                  </Box>
                  <Typography variant="body2" sx={{ color: "#374151", lineHeight: 1.6, textDecoration: completed ? "line-through" : "none" }}>
                    {item.note}
                  </Typography>
                  {item.user_id && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                      — {getUserName(item.user_id)}
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
});

export default LeadActivity;
